import React, { createContext, useContext, useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, get, set, push, remove, onValue, off } from 'firebase/database';
import { useAuth } from './AuthContext';

const SlideshowContext = createContext();

export function useSlideshow() {
  return useContext(SlideshowContext);
}

export function SlideshowProvider({ children }) {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser, isAdmin } = useAuth();

  // Fetch slideshow images from Firebase
  useEffect(() => {
    setLoading(true);
    setError('');
    
    const slideshowRef = ref(database, 'slideshow');
    
    // Create a realtime listener for slideshow
    const unsubscribe = onValue(slideshowRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const slideshowData = snapshot.val();
          const slidesList = Object.keys(slideshowData)
            .map(key => ({
              id: key,
              title: slideshowData[key].title || '',
              description: slideshowData[key].description || '',
              imageBase64: slideshowData[key].imageBase64 || null,
              createdAt: slideshowData[key].createdAt || Date.now(),
              active: slideshowData[key].active !== false, // Default to true if not specified
              order: slideshowData[key].order || 0
            }))
            .sort((a, b) => a.order - b.order); // Sort by order
          
          setSlides(slidesList);
        } else {
          // No slides exist, not an error
          setSlides([]);
        }
        setError('');
      } catch (err) {
        console.error('Error processing slideshow data:', err);
        setError('Failed to process slideshow data');
      } finally {
        setLoading(false);
      }
    }, (err) => {
      console.error('Error fetching slideshow:', err);
      setError('Failed to load slideshow: ' + err.message);
      setLoading(false);
    });
    
    // Clean up the listener when the component unmounts
    return () => {
      off(slideshowRef);
    };
  }, []);

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Add a new slideshow image
  const addSlide = async (title, description, imageFile) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can add slideshow images');
    }

    if (!imageFile) {
      throw new Error('Image is required for slideshow');
    }

    try {
      // Convert image to base64
      const imageBase64 = await convertToBase64(imageFile);
      
      // Get current slides count for ordering
      const slideshowRef = ref(database, 'slideshow');
      const snapshot = await get(slideshowRef);
      const currentOrder = snapshot.exists() ? Object.keys(snapshot.val()).length : 0;
      
      const newSlideRef = push(slideshowRef);
      
      const slide = {
        title,
        description,
        imageBase64,
        createdAt: Date.now(),
        active: true,
        order: currentOrder
      };
      
      await set(newSlideRef, slide);
      
      return { success: true, id: newSlideRef.key };
    } catch (err) {
      console.error('Error adding slide:', err);
      throw err;
    }
  };

  // Edit a slideshow image
  const editSlide = async (id, { title, description, imageFile, active, order }) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can edit slideshow images');
    }

    try {
      const slideRef = ref(database, `slideshow/${id}`);
      
      // Get the current data first
      const snapshot = await get(slideRef);
      if (!snapshot.exists()) {
        throw new Error('Slide not found');
      }
      
      const currentData = snapshot.val();
      
      // Process new image if provided
      let imageBase64 = currentData.imageBase64;
      if (imageFile) {
        imageBase64 = await convertToBase64(imageFile);
      }
      
      // Update data
      const updatedData = {
        ...currentData,
        title: title !== undefined ? title : currentData.title,
        description: description !== undefined ? description : currentData.description,
        imageBase64,
        active: active !== undefined ? active : currentData.active,
        order: order !== undefined ? order : currentData.order
      };
      
      await set(slideRef, updatedData);
      
      return { success: true };
    } catch (err) {
      console.error('Error editing slide:', err);
      throw err;
    }
  };

  // Delete a slideshow image
  const deleteSlide = async (id) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can delete slideshow images');
    }

    try {
      // Delete the slide from the database
      const slideRef = ref(database, `slideshow/${id}`);
      await remove(slideRef);
      
      return { success: true };
    } catch (err) {
      console.error('Error deleting slide:', err);
      throw err;
    }
  };

  // Reorder slides
  const reorderSlides = async (reorderedSlides) => {
    if (!currentUser || !isAdmin) {
      throw new Error('Only admins can reorder slideshow images');
    }

    try {
      const updates = {};
      
      reorderedSlides.forEach((slide, index) => {
        updates[`slideshow/${slide.id}/order`] = index;
      });
      
      const dbRef = ref(database);
      await set(dbRef, updates);
      
      return { success: true };
    } catch (err) {
      console.error('Error reordering slides:', err);
      throw err;
    }
  };

  // Get only active slides for display
  const getActiveSlides = () => {
    return slides.filter(slide => slide.active);
  };

  const value = {
    slides,
    loading,
    error,
    addSlide,
    editSlide,
    deleteSlide,
    reorderSlides,
    getActiveSlides
  };

  return (
    <SlideshowContext.Provider value={value}>
      {children}
    </SlideshowContext.Provider>
  );
} 